// src/components/AccountInfo.js

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Heading,
  Button,
  Box,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  Code,
  VStack,
} from '@chakra-ui/react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuthToken } from '../hooks/useAuthToken';
import { apiConfig, auth0Config } from '../config/auth.config';

const AccountInfo = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userApiKey, setUserApiKey] = useState('');
  const [tokenDebug, setTokenDebug] = useState(null);
  
  const { user, getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const { getToken } = useAuthToken();

  // Debug function to show token contents
  const debugTokens = async () => {
    try {
      // Get ID token claims
      const idClaims = await getIdTokenClaims();
      
      // Get access token with audience
      const accessToken = await getAccessTokenSilently({
        authorizationParams: {
          audience: auth0Config.audience,
        }
      });
      
      // Decode access token
      const accessPayload = JSON.parse(atob(accessToken.split('.')[1]));
      
      setTokenDebug({
        idTokenClaims: {
          role: idClaims?.['https://indexerglue.com/role'],
          plan: idClaims?.['https://indexerglue.com/plan'],
          user_id: idClaims?.['https://indexerglue.com/user_id'],
        },
        accessTokenClaims: {
          role: accessPayload['https://indexerglue.com/role'],
          plan: accessPayload['https://indexerglue.com/plan'],
          user_id: accessPayload['https://indexerglue.com/user_id'],
          aud: accessPayload.aud,
          permissions: accessPayload.permissions,
        }
      });
    } catch (err) {
      setTokenDebug({ error: err.message });
    }
  };

  // Function to fetch user API key from the backend
  const fetchUserApiKey = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getToken();

      const apiKeyResponse = await axios.get(`${apiConfig.baseUrl}/get_api_key`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setUserApiKey(apiKeyResponse.data.api_key);
    } catch (error) {
      console.error('Error fetching user API key:', error);
      setError('Failed to load user API key.');
    } finally {
      setLoading(false);
    }
  };

  const addUser = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getToken();

      await axios.post(
        `${apiConfig.baseUrl}/create_user`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      // After creating user, fetch their API key
      await fetchUserApiKey();
    } catch (error) {
      console.error('Error creating user', error);
      setError('Error creating user');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserApiKey();
  }, []);

  return (
    <Box mt={8}>
      <Heading mb={4}>Your Auth Info</Heading>
      <Text>{user?.email || 'No email available'}</Text>
      
      <Heading mb={4} mt={6}>Your User API Key</Heading>
      {loading ? (
        <Spinner size="xl" />
      ) : (
        <>
          {userApiKey ? (
            <Text>{userApiKey}</Text>
          ) : (
            <Text>Your user doesn't exist yet. Create your user account below</Text>
          )}
        </>
      )}
      
      <Box mt={6}>
        <Button colorScheme="teal" onClick={addUser} isLoading={loading}>
          Create and add user
        </Button>
      </Box>

      {error && (
        <Alert status="error" mb={4} mt={4}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      {/* Debug Section - shows token claims */}
      <Box mt={8} p={4} bg="gray.100" borderRadius="md">
        <Heading size="md" mb={4}>🔧 Token Debug Info</Heading>
        <Button colorScheme="blue" onClick={debugTokens} size="sm" mb={4}>
          Load Token Claims
        </Button>
        {tokenDebug && (
          <VStack align="start" spacing={4}>
            <Box>
              <Text fontWeight="bold">ID Token Claims:</Text>
              <Code display="block" whiteSpace="pre" p={2}>
                {JSON.stringify(tokenDebug.idTokenClaims, null, 2)}
              </Code>
            </Box>
            <Box>
              <Text fontWeight="bold">Access Token Claims:</Text>
              <Code display="block" whiteSpace="pre" p={2}>
                {JSON.stringify(tokenDebug.accessTokenClaims, null, 2)}
              </Code>
            </Box>
          </VStack>
        )}
      </Box>
    </Box>
  );
};

export default AccountInfo;
